import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import { formatGMV } from 'lib/utils/FormatUtils';
import { constructUrlPathSegment } from 'components/upb_viewer/UPBViewer';

import {
  BG_2,
  BORDER_2,
  FG_1,
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_XS,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
  GLOBAL_NAV_HEIGHT,
  KPI_STRIP_HEIGHT,
  PRIMARY_COLOR,
  PRIMARY_HOVER,
  PRIMARY_LIGHT,
  RADIUS_FULL,
  RADIUS_LG,
  RADIUS_MD,
  SHADOW_XS,
  TOPBAR_HEIGHT,
  WHITE,
  WHITE_OVERLAY,
} from 'components/upb_viewer_beta/css/betaTokens';

const GRID_ICON_SVG = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16, display: 'block' }}
  >
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

const BOX_ICON_SVG = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 16, height: 16, display: 'block' }}
  >
    <path d="M3 7l9-4 9 4v10l-9 4-9-4z" />
    <path d="M3 7l9 4 9-4M12 11v10" />
  </svg>
);

const CHEVRON_SVG = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ width: 14, height: 14, display: 'block' }}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const labelOf = c => c.taxonomy_display_name || c.category_name || '';
const seg = c => constructUrlPathSegment({ value: c.id, label: labelOf(c) });

/**
 * BetaL1Sidebar — sticky card-style category list for UPB Viewer Beta.
 *
 * Shows "All Categories" at the top and then each L1 category.
 * Clicking an item calls onSelect(id) — null means "All".
 */
function BetaL1Sidebar({
  categories,
  loading,
  selectedL1Id,
  selectedL2Id,
  searchActive,
  expandedL1Id,
  onSelect,
  onToggleExpand,
  onNavigate,
  baseURL,
  gmvByL1Id,
  gmvByL2Id,
  totalGmv,
}) {
  // While a search is active the main pane shows search results rather than a
  // category, so nothing in the sidebar should read as selected — including
  // "All Categories", which would otherwise light up once the L1/L2 highlight
  // is suppressed. The URL still holds the category the user was standing in,
  // so clearing the search restores this highlight exactly as it was.
  const isAllActive = !searchActive && selectedL1Id === null && selectedL2Id == null;
  const navMode = typeof onNavigate === 'function';

  const handleAllClick = () => {
    // Prefer the filter callback (root/funnel); fall back to navigation off-root.
    if (onSelect) onSelect(null);
    else if (navMode) onNavigate(`/${baseURL}`);
  };

  const handleL1RowClick = cat => {
    if (onSelect) onSelect(cat.id);
    else if (navMode) onNavigate(`/${baseURL}/${seg(cat)}`);
  };

  const handleExpandClick = (e, catId) => {
    e.stopPropagation();
    if (onToggleExpand) onToggleExpand(catId);
  };

  const handleL2Click = (e, l1, l2) => {
    e.stopPropagation();
    if (navMode) onNavigate(`/${baseURL}/${seg(l1)}/${seg(l2)}`);
  };

  return (
    <div className={css(styles.card)}>
      <div className={css(styles.catHead)}>
        <div className={css(styles.catHeadRow)}>
          <span className={css(styles.catTitle)}>All Categories</span>
        </div>
      </div>

      <div className={css(styles.catList)}>
        <button
          type="button"
          className={css(styles.catRow, isAllActive && styles.catRowActive)}
          onClick={handleAllClick}
        >
          <div className={css(styles.catIco, isAllActive && styles.catIcoActive)}>
            {GRID_ICON_SVG}
          </div>
          <span className={css(styles.catName, isAllActive && styles.catNameActive)}>
            All Categories
          </span>
          {totalGmv > 0 && (
            <span className={css(styles.catGmv, isAllActive && styles.catGmvActive)}>
              {formatGMV(totalGmv)}
            </span>
          )}
        </button>

        {loading && <div className={css(styles.loadingText)}>Loading…</div>}

        {categories.map(cat => {
          const isActive = !searchActive && selectedL1Id === cat.id;
          const subOptions = Array.isArray(cat.sub_options) ? cat.sub_options : [];
          const hasChildren = subOptions.length > 0;
          const isExpanded = expandedL1Id === cat.id;
          return (
            <React.Fragment key={cat.id}>
              <button
                type="button"
                className={css(styles.catRow, isActive && styles.catRowActive)}
                onClick={() => handleL1RowClick(cat)}
              >
                <div className={css(styles.catIco, isActive && styles.catIcoActive)}>
                  {cat.taxonomy_image ? (
                    <img
                      src={cat.taxonomy_image}
                      alt=""
                      style={{ width: 28, height: 28, objectFit: 'contain' }}
                    />
                  ) : (
                    BOX_ICON_SVG
                  )}
                </div>
                <span className={css(styles.catName, isActive && styles.catNameActive)}>
                  {labelOf(cat)}
                </span>
                {gmvByL1Id[cat.id] > 0 && (
                  <span className={css(styles.catGmv, isActive && styles.catGmvActive)}>
                    {formatGMV(gmvByL1Id[cat.id])}
                  </span>
                )}
                <span className={css(styles.catBadge, isActive && styles.catBadgeActive)}>L1</span>
                {hasChildren && onToggleExpand && (
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    className={css(styles.catChevron, isExpanded && styles.catChevronOpen)}
                    onClick={e => handleExpandClick(e, cat.id)}
                  >
                    {CHEVRON_SVG}
                  </span>
                )}
              </button>

              {isExpanded &&
                hasChildren &&
                subOptions.map(l2 => {
                  const isL2Active =
                    !searchActive && selectedL2Id != null && Number(selectedL2Id) === l2.id;
                  const l2Gmv = gmvByL2Id[l2.id];
                  return (
                    <button
                      key={l2.id}
                      type="button"
                      className={css(styles.catChildRow, isL2Active && styles.catChildRowActive)}
                      onClick={e => handleL2Click(e, cat, l2)}
                    >
                      <span
                        className={css(
                          styles.catChildName,
                          isL2Active && styles.catChildNameActive,
                        )}
                      >
                        {labelOf(l2)}
                      </span>
                      {l2Gmv > 0 && (
                        <span
                          className={css(styles.catGmv, isL2Active && styles.catChildGmvActive)}
                        >
                          {formatGMV(l2Gmv)}
                        </span>
                      )}
                      <span
                        className={css(styles.catBadge, isL2Active && styles.catChildBadgeActive)}
                      >
                        L2
                      </span>
                    </button>
                  );
                })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// The sidebar lives inside the content grid, which is itself the scroll container
// positioned below the fixed topbar + KPI strip. Sticky offset is therefore
// relative to the grid (top of its scroll area), not the viewport.
const STICKY_TOP = 0;
const CARD_HEIGHT = `calc(100vh - ${GLOBAL_NAV_HEIGHT + TOPBAR_HEIGHT + KPI_STRIP_HEIGHT + 60}px)`;

const styles = StyleSheet.create({
  card: {
    backgroundColor: WHITE,
    border: `1px solid ${BORDER_2}`,
    borderRadius: RADIUS_LG,
    boxShadow: SHADOW_XS,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: STICKY_TOP,
    height: CARD_HEIGHT,
    minHeight: 560,
  },

  catHead: {
    padding: '14px 16px 10px',
    borderBottom: `1px solid ${BORDER_2}`,
    flexShrink: 0,
  },

  catHeadRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  catTitle: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: 14,
    color: FG_1,
  },

  catList: {
    flex: 1,
    overflowY: 'auto',
    padding: '6px 8px',
  },

  catRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: 8,
    borderRadius: RADIUS_MD,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    textAlign: 'left',
    outline: 'none',
    ':hover': {
      backgroundColor: BG_2,
    },
    ':focus-visible': {
      boxShadow: `0 0 0 2px ${PRIMARY_COLOR}`,
    },
  },

  catRowActive: {
    backgroundColor: PRIMARY_LIGHT,
    // Active state already communicates selection — suppress the keyboard
    // focus ring so it doesn't visually merge with an adjacent active row.
    ':focus-visible': {
      boxShadow: 'none',
    },
  },

  catIco: {
    width: 28,
    height: 28,
    borderRadius: 7,
    backgroundColor: BG_2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: FG_3,
    overflow: 'hidden',
  },

  catIcoActive: {
    backgroundColor: WHITE,
    color: PRIMARY_COLOR,
  },

  catName: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  catNameActive: {
    color: PRIMARY_HOVER,
    fontWeight: FONT_WEIGHT_BOLD,
  },

  catBadge: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: 9,
    color: FG_3,
    backgroundColor: BG_2,
    padding: '2px 6px',
    borderRadius: RADIUS_FULL,
    letterSpacing: '0.04em',
    flexShrink: 0,
  },

  catBadgeActive: {
    backgroundColor: WHITE,
    color: PRIMARY_HOVER,
  },

  catGmv: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },

  catGmvActive: {
    color: PRIMARY_HOVER,
  },

  loadingText: {
    padding: '16px 8px',
    fontFamily: FONT_BODY,
    fontWeight: 400,
    fontSize: FONT_SIZE_XS,
    color: FG_3,
  },

  catChevron: {
    width: 22,
    height: 22,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: FG_3,
    flexShrink: 0,
    cursor: 'pointer',
    transition: 'transform 0.15s, background 0.15s',
    ':hover': {
      backgroundColor: BG_2,
      color: FG_1,
    },
  },

  catChevronOpen: {
    transform: 'rotate(180deg)',
  },

  catChildRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px 6px 40px',
    // Small top margin so consecutive rows (and the L1 parent above the
    // first child) don't visually merge when both have fills.
    marginTop: 2,
    borderRadius: RADIUS_MD,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    textAlign: 'left',
    outline: 'none',
    ':hover': {
      backgroundColor: BG_2,
    },
    ':focus-visible': {
      boxShadow: `0 0 0 2px ${PRIMARY_COLOR}`,
    },
  },

  catChildRowActive: {
    // Active L2 uses the solid primary purple (mockup .cat-child.active) so it
    // visually contrasts with the L1 parent above (which uses PRIMARY_LIGHT
    // when expanded) — otherwise both rows merge into one purple block.
    backgroundColor: PRIMARY_COLOR,
    ':hover': {
      backgroundColor: PRIMARY_HOVER,
    },
    ':focus-visible': {
      boxShadow: 'none',
    },
  },

  catChildName: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    color: FG_1,
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  // Active-L2 styles use white text/badges on the solid primary purple fill.
  catChildNameActive: {
    color: WHITE,
    fontWeight: FONT_WEIGHT_BOLD,
  },

  catChildGmvActive: {
    color: WHITE,
  },

  catChildBadgeActive: {
    backgroundColor: WHITE_OVERLAY,
    color: WHITE,
  },
});

BetaL1Sidebar.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  selectedL1Id: PropTypes.number,
  selectedL2Id: PropTypes.number,
  searchActive: PropTypes.bool,
  expandedL1Id: PropTypes.number,
  onSelect: PropTypes.func,
  onToggleExpand: PropTypes.func,
  onNavigate: PropTypes.func,
  baseURL: PropTypes.string,
  gmvByL1Id: PropTypes.object,
  gmvByL2Id: PropTypes.object,
  totalGmv: PropTypes.number,
};

BetaL1Sidebar.defaultProps = {
  categories: [],
  loading: false,
  selectedL1Id: null,
  selectedL2Id: null,
  searchActive: false,
  expandedL1Id: null,
  onSelect: null,
  onToggleExpand: null,
  onNavigate: null,
  baseURL: 'upb-viewer-beta',
  gmvByL1Id: {},
  gmvByL2Id: {},
  totalGmv: 0,
};

export default BetaL1Sidebar;
